import styled from '@emotion/styled'
import { Typography } from '@mui/material'
import React from 'react'

import { formatRating } from '@js/common/utils'

import { CTAButtonWithWT } from '@f-fertighaus/applications/Next/components/common/CTAButton'
import {
  DesktopOnly,
  MobileOnly
} from '@f-fertighaus/applications/Next/components/common/MediaQuery'
import { ChatIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/ChatIcon'
import { shadow } from '@f-fertighaus/applications/Next/config/styles'
import { t } from '@f-fertighaus/applications/Next/public/locales/de'
import { useHouseDetail } from '@f-fertighaus/applications/Next/utils/context/HouseDetail'
import { ratingLabelByScore } from '@f-fertighaus/applications/Next/utils/ratingLabelByScore'
import { Rating as StarRating } from '@f-fertighaus/components/ReviewRating/SearchResultRating'

const getRatingText = (rating: number) => {
  if (rating < 1.25) {
    return t(`houseDetailPage.vendor.response-low-rating`)
  }
  if (rating < 3.75) {
    return t(`houseDetailPage.vendor.response-medium-rating`)
  }
  return t(`houseDetailPage.vendor.response-high-rating`)
}

export const VendorRatingSection = () => {
  return (
    <>
      <MobileOnly>
        <VendorRatingSectionMobile />
      </MobileOnly>
      <DesktopOnly>
        <VendorRatingSectionDesktop />
      </DesktopOnly>
    </>
  )
}

export const VendorRatingSectionMobile = () => {
  const { state, vendor } = useHouseDetail()

  if (!state.review_data) {
    return null
  }

  const { avg_rating, count_total, response_rate } = state.review_data

  return (
    <FullBleed>
      <Wrapper>
        <VendorLogoBackground>
          <img src={vendor.logo['max-320x240.noauto']} alt='vendor-logo' />
        </VendorLogoBackground>
        <RatingWrapper>
          <Typography variant='body' component='p' fontWeight='fontWeightBold'>
            {t('houseDetailPage.vendor.customers-opinions')}
          </Typography>
          <RatingStars>
            <RatingStarsWrapper>
              <StarRating
                value={avg_rating}
                size={18}
                uniqueName='vendor-section-rating--mobile'
              />
            </RatingStarsWrapper>
            <Typography variant='mediumTitle' component='p' marginLeft={2}>
              {formatRating(avg_rating)}
            </Typography>
            <Typography
              variant='subtitle1'
              marginLeft={1}
              color='primary.light'>
              {`(${count_total})`}
            </Typography>
          </RatingStars>
        </RatingWrapper>
        <Typography variant='body2' sx={{ marginLeft: '-108px' }}>
          <span style={{ marginRight: '8px' }}>
            <ChatIcon />
          </span>
          {typeof response_rate === 'number'
            ? getRatingText(avg_rating)
            : t(`houseDetailPage.vendor.response-rate-none`)}
        </Typography>
      </Wrapper>
    </FullBleed>
  )
}

export const VendorRatingSectionDesktop = () => {
  const { state, vendor } = useHouseDetail()
  const isDisabled = !state.active

  if (!state.review_data) {
    return null
  }

  const { avg_rating, count_total, response_rate } = state.review_data

  return (
    <Wrapper>
      <VendorLogoBackground>
        <img src={vendor.logo['max-320x240.noauto']} alt='vendor-logo' />
      </VendorLogoBackground>
      <RatingWrapper>
        <Typography
          variant='bodyTitle'
          component='p'
          fontWeight='fontWeightBold'>
          {t('houseDetailPage.vendor.customers-opinions')}
        </Typography>

        <RatingStars>
          <RatingStarsWrapper>
            <StarRating
              value={avg_rating}
              size={24}
              uniqueName='vendor-section-rating--desktop'
            />
          </RatingStarsWrapper>
          <Typography variant='mediumTitle' component='p' marginLeft={2}>
            {formatRating(avg_rating)}
          </Typography>
        </RatingStars>
        <Typography variant='body' sx={{ textWrap: 'nowrap' }}>
          {t(
            `houseDetailPage.vendor.${
              count_total === 1 ? 'reviews-amount-single' : 'reviews-amount'
            }`,
            { amount: count_total }
          )}
          <RatingLabel>
            {t(
              `houseDetailPage.vendor.review-label.${ratingLabelByScore(
                avg_rating
              )}`
            )}
          </RatingLabel>
        </Typography>
      </RatingWrapper>
      <ActionsWrapper>
        <Typography variant='body2' marginBottom={2} textAlign='right'>
          <span style={{ marginRight: '8px' }}>
            <ChatIcon />
          </span>
          {typeof response_rate === 'number'
            ? getRatingText(avg_rating)
            : t(`houseDetailPage.vendor.response-rate-none`)}
        </Typography>
        <CTAButtonWithWT
          disabled={isDisabled}
          className='js-request-button'
          data-request-type='house-detail-form'
          data-funnel-variant='house-wizard-item-catalog'
          variant='outlined'
          color='primary'
          wtPayload={{
            category: 'CTA-button',
            action: 'click',
            label: 'catalog-request',
            indicates: ['funnel-entry'],
            location: 'vendor-section',
            values: [
              {
                type: 'text',
                value: t('houseDetailPage.vendor.request-catalog')
              }
            ]
          }}
          {...(state.vendor_nid && { 'data-vendor-nid': state.vendor_nid })}>
          {t('houseDetailPage.vendor.request-catalog')}
        </CTAButtonWithWT>
      </ActionsWrapper>
    </Wrapper>
  )
}

const VendorLogoBackground = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 90px;
  height: 90px;
  background-color: ${({ theme }) => theme.palette.background.paper};
  box-shadow: 2px 2px 5px 0 rgb(0 0 0 / 10%);
  padding: 4px;
  top: -16px;
  left: 0;
  border-radius: var(--border-radius-card-large);

  img {
    object-fit: contain;
    object-position: center;
    max-height: 100%;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    width: 150px;
    height: 150px;
    left: 16px;
    top: -48px;
    padding: 10px;
  }
`

const FullBleed = styled.div`
  margin: 0 -16px;
  padding: 0 16px;
  background: ${({ theme }) => theme.palette.glass[500]};
  box-shadow: ${shadow.actionCard};
`

const Wrapper = styled.div`
  position: relative;
  padding-left: 108px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  column-gap: 8px;
  padding-bottom: 8px;
  margin-bottom: 0;

  ${({ theme }) => theme.breakpoints.up('md')} {
    flex-direction: row;
    padding-left: 190px;
  }
`

const RatingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 16px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-top: 0;
  }
`

const RatingStars = styled.div`
  display: flex;
  align-items: center;
`
const RatingStarsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
`
const RatingLabel = styled.strong`
  margin-left: 8px;

  &:before {
    content: '·';
    margin-right: 8px;
  }
`

const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`
